import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const downloadTokenRef = useRef(
    (() => { try { return sessionStorage.getItem('_at'); } catch { return null; } })()
  );

  const saveToken = (token) => {
    downloadTokenRef.current = token;
    try { if (token) sessionStorage.setItem('_at', token); else sessionStorage.removeItem('_at'); } catch (e) { console.warn('sessionStorage unavailable:', e?.message); }
  };

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });

  // Add Authorization header as backup when cookies don't work (sandboxed iframes)
  api.interceptors.request.use((config) => {
    if (downloadTokenRef.current) {
      config.headers.Authorization = `Bearer ${downloadTokenRef.current}`;
    }
    return config;
  });

  // Handle 401 errors (only clear session for user-initiated requests, not initial fetch)
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401 && error.config?._userInitiated) {
        setUser(null);
        setProfile(null);
        saveToken(null);
      }
      return Promise.reject(error);
    }
  );

  const fetchUser = useCallback(async () => {
    if (!downloadTokenRef.current) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/auth/me');
      setUser(response.data.user);
      setProfile(response.data.profile);
      if (response.data.access_token) {
        saveToken(response.data.access_token);
      }
    } catch {
      setUser(null);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password }, { _userInitiated: true });
    const { access_token, user: userData, profile: profileData } = response.data;
    saveToken(access_token);
    setUser(userData);
    setProfile(profileData);
    return response.data;
  };

  const registerPatient = async (data) => {
    const response = await api.post('/auth/register/patient', data);
    const { access_token, user: userData, profile: profileData } = response.data;
    saveToken(access_token);
    setUser(userData);
    setProfile(profileData);
    return response.data;
  };

  const registerProvider = async (data) => {
    const response = await api.post('/auth/register/provider', data);
    const { access_token, user: userData, profile: profileData } = response.data;
    saveToken(access_token);
    setUser(userData);
    setProfile(profileData);
    return response.data;
  };

  /**
   * Hydrate the auth state from an already-issued session (e.g. /claim/finalize
   * or any other backend flow that returns access_token + user).
   * Triggers a /auth/me refresh in the background so `profile` is filled too.
   */
  const applySession = useCallback(async ({ access_token, user: userData }) => {
    if (access_token) saveToken(access_token);
    if (userData) setUser(userData);
    try {
      const response = await api.get('/auth/me');
      setUser(response.data.user);
      setProfile(response.data.profile);
    } catch {
      /* cookie/token still works — profile will load on next mount */
    }
  }, []);

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Server logout failed, clear state anyway
    }
    saveToken(null);
    setUser(null);
    setProfile(null);
  };

  // Build download URL with token for file downloads
  const getDownloadUrl = (path) => {
    const token = downloadTokenRef.current;
    const separator = path.includes('?') ? '&' : '?';
    return `${API_URL}/api${path}${token ? `${separator}token=${token}` : ''}`;
  };

  // Trigger file download silently (no popups, no new tabs)
  const triggerDownload = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: 'blob', timeout: 120000 });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      // Method 1: File System Access API (bypasses sandbox restrictions)
      if (typeof window.showSaveFilePicker === 'function') {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename || 'download.xlsx',
            types: [{
              description: 'Excel',
              accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] },
            }],
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return;
        } catch (e) {
          if (e.name === 'AbortError') return;
          // If SecurityError, fall through to Method 2
        }
      }

      // Method 2: Blob URL + hidden anchor
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || 'download.xlsx';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 3000);
    } catch (err) {
      if (err.response?.status === 401) {
        alert('Sessao expirada. Faca login novamente.');
        saveToken(null);
        setUser(null);
        setProfile(null);
      } else {
        alert('Erro ao baixar: ' + err.message);
      }
    }
  };

  const isAuthenticated = !!user;
  const isPatient = user?.role === 'patient';
  const isProvider = user?.role === 'provider';
  const isAdmin = user?.role === 'admin';

  const value = {
    user,
    profile,
    loading,
    isAuthenticated,
    isPatient,
    isProvider,
    isAdmin,
    login,
    logout,
    registerPatient,
    registerProvider,
    applySession,
    api,
    refreshUser: fetchUser,
    getDownloadUrl,
    triggerDownload,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
